import { Component, EventEmitter, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { JobDigitalLogFormField, JobDigitalLogFormFieldDoc, JobDigitalLogFormTimeSlot, DigitalLogFieldDocumentComponent, JobDigitalLogFieldUploadDocumentPayload, FieldDocumentListOrderItemPayload, FieldDocumentListOrderItem } from '@vitralogy/ng-digital-log';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ButtonIcons } from 'src/app/components/action-button/models/button-icons.enum';
import { GenericModalComponent } from 'src/app/components/generic-modal/generic-modal.component';
import { FieldAttachment } from 'src/app/models/digital-log/field-attachment';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { DigitalLogService } from 'src/app/services/sections/job/digital-log.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';

@Component({
	selector: 'fields-attachments-list',
	templateUrl: './fields-attachments-list.component.html',
	styleUrls: ['./fields-attachments-list.component.scss', '../../../../stylesheets/job-main.scss', '../../../../stylesheets/job-actions.scss', '../../../../stylesheets/job-inputs.scss', '../../../../stylesheets/job-form.scss']
})
export class FieldsAttachmentsListComponent implements OnInit, OnDestroy {
	@ViewChild("fieldDocumentErrorModal") fieldDocumentErrorModal: GenericModalComponent;

	public readonly ButtonIcons = ButtonIcons;
	public fieldsAttachments: FieldAttachment[] = [];
	public searchForm: FormGroup = this.formBuilder.group({
		searchKeyword: [''],
		displayFavoritesOnly: [false],
		displayOnlyNoDocs: [false],
	});
	public isLoading = false;

	public fieldDocuments$: BehaviorSubject<JobDigitalLogFormFieldDoc[]> = new BehaviorSubject<JobDigitalLogFormFieldDoc[]>([]);
	public isfieldDocumentsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public formTimeSlots$: BehaviorSubject<JobDigitalLogFormTimeSlot[]> = new BehaviorSubject<JobDigitalLogFormTimeSlot[]>([]);

	public onUploadFieldDocument: EventEmitter<JobDigitalLogFieldUploadDocumentPayload> = new EventEmitter();
	public onRemoveFieldDocument: EventEmitter<{ jobDigitalLogFormFieldDocId: number, digitalLogFormFieldId: number, ytdReportYear: number, jobDigitalLogFormTimeSlotId: number }> = new EventEmitter();
	public onEditFieldDocument: EventEmitter<JobDigitalLogFieldUploadDocumentPayload> = new EventEmitter();
	public onFieldDocumentListOrderChange: EventEmitter<FieldDocumentListOrderItemPayload> = new EventEmitter();


	private currentField: FieldAttachment = {};
	private currentFieldDocs: JobDigitalLogFormFieldDoc[] = [];
	private unsubscribe = new Subject<void>();
	private currentForm: any = {};

	constructor(
		private activeRoute: ActivatedRoute,
		private router: Router,
		private matDialog: MatDialog,
		private activatedRoute: ActivatedRoute,
		private formBuilder: FormBuilder,
		private digitalLogService: DigitalLogService,
	) { }

	ngOnInit(): void {
		GlobalSettingsService.isSecondaryLoaderLoading = false;
		GlobalSettingsService.isGlobalLoaderLoading = false;

		this.fieldsAttachments = this.activatedRoute.snapshot.data.fields;

		this.initSearchForm();
		this.registerDocumentsModalEvents();
	}

	public uploadFieldDocument(uploadedFieldDocument: JobDigitalLogFieldUploadDocumentPayload): void {
		if (uploadedFieldDocument.jobDigitalLogFormTimeSlot.jobDigitalLogFormTimeSlotId === null) {
			uploadedFieldDocument.jobDigitalLogFormTimeSlot.jobDigitalLogFormTimeSlotId = 0;
		}

		this.isfieldDocumentsLoading$.next(true);
		const data = {
			digitalLogFormFieldId: uploadedFieldDocument.digitalLogFormFieldId,
			documentClassAndTypeMappingId: uploadedFieldDocument.documentClassAndTypeMappingId,
			file: uploadedFieldDocument.file,
			timeSlot: uploadedFieldDocument.jobDigitalLogFormTimeSlot,
			documentContainerId: uploadedFieldDocument.documentContainerId || 0,
			documentLabel: uploadedFieldDocument.documentLabel,
			description: uploadedFieldDocument.description,
			listOrder: uploadedFieldDocument.listOrder,
			isDeprecated: uploadedFieldDocument.isDeprecated
		};

		this.digitalLogService.saveFieldDocument(
			TokenHelper.getToken(this.activatedRoute),
			this.currentField.jobDigitalLogFormRequestId,
			uploadedFieldDocument.ytdReportYear,
			data
		)
			.subscribe(
				response => {
					if (!uploadedFieldDocument.jobDigitalLogFormTimeSlot.jobDigitalLogFormTimeSlotId && !!response.jobDigitalLogFormTimeSlot.jobDigitalLogFormTimeSlotId) {
						this.onTimeSlotUpdated(response.jobDigitalLogFormTimeSlot);
					}

					if (uploadedFieldDocument.fieldDocument?.jobDigitalLogFormFieldDocId > 0) {
						this.currentFieldDocs = this.currentFieldDocs.map(doc => {
							return doc.jobDigitalLogFormFieldDocId === uploadedFieldDocument.fieldDocument.jobDigitalLogFormFieldDocId
								? response.fieldDocument
								: doc;
						});
					}
					else {
						this.currentFieldDocs = [
							...this.currentFieldDocs,
							response.fieldDocument
						];
					}

					this.fieldDocuments$.next(this.currentFieldDocs);
					this.isfieldDocumentsLoading$.next(false);
				},
				() => {
					this.isfieldDocumentsLoading$.next(false);

					this.fieldDocumentErrorModal.open();
					this.currentFieldDocs = [...this.currentFieldDocs];
					this.fieldDocuments$.next(this.currentFieldDocs);
				});
	}

	public removeFieldDocument(data: { jobDigitalLogFormFieldDocId: number, digitalLogFormFieldId: number, ytdReportYear: number, jobDigitalLogFormTimeSlotId: number }): void {
		this.isfieldDocumentsLoading$.next(true);

		this.digitalLogService.deleteFieldDocument(
			TokenHelper.getToken(this.activatedRoute),
			this.currentField.jobDigitalLogFormRequestId,
			data.ytdReportYear,
			data.digitalLogFormFieldId,
			data.jobDigitalLogFormFieldDocId,
			data.jobDigitalLogFormTimeSlotId
		).subscribe(
			() => {
				this.currentFieldDocs = this.currentFieldDocs.filter(doc => doc.jobDigitalLogFormFieldDocId !== data.jobDigitalLogFormFieldDocId);
				this.fieldDocuments$.next(this.currentFieldDocs);
				this.isfieldDocumentsLoading$.next(false);
			},
			() => {
				this.isfieldDocumentsLoading$.next(false);

				this.fieldDocumentErrorModal.open();
				this.currentFieldDocs = [...this.currentFieldDocs];
				this.fieldDocuments$.next(this.currentFieldDocs);
			});
	}

	initSearchForm() {
		this.searchForm
			.valueChanges
			.pipe(
				debounceTime(500),
				distinctUntilChanged(
					(a, b) => JSON.stringify(a) === JSON.stringify(b)
				),
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				() => {
					this.getFieldsAttchaments();
				}
			);
	}

	private onTimeSlotUpdated(timeSlot: JobDigitalLogFormTimeSlot): void {
		this.formTimeSlots$.next([timeSlot]);
	}

	getFieldsAttchaments() {
		this.isLoading = true;
		this.searchForm.disable();

		this.digitalLogService
			.getFieldsAttachments(
				this.activeRoute.snapshot.paramMap.get('token'),
				this.searchForm.value)
			.pipe(
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				result => {
					this.fieldsAttachments = result;
					this.isLoading = false;
					this.searchForm.enable();
				},
				() => {
					this.isLoading = false;
					this.searchForm.enable();
					this.fieldsAttachments = [];
				}
			);
	}

	onSearchClear() {
		this.searchForm.patchValue({ searchKeyword: '' })
	}

	onDocumentsClick(field) {
		const f = {
			digitalLogFormId: field.digitalLogFormId,
			digitalLogFormFieldId: field.digitalLogFormFieldId,
			name: field.fieldName,
			documentClassAndTypeMappingId: field.documentClassAndTypeMappingId,

		} as JobDigitalLogFormField;

		this.isfieldDocumentsLoading$.next(true);
		this.currentField = field;

		this.getFieldDocuments(field.jobDigitalLogFormRequestId, field.digitalLogFormFieldId);
		this.formTimeSlots$.next([new JobDigitalLogFormTimeSlot(field.jobDigitalLogFormRequestId, field.originalDate, field.overwriteDate, field.jobDigitalLogFormTimeSlotId)]);

		const config = <any>{
			isLoading: this.isfieldDocumentsLoading$.asObservable(),
			field: f,
			documents: this.fieldDocuments$.asObservable(),
			timeSlots: this.formTimeSlots$.asObservable(),
			parentComponent: <any>this
		};
		const fieldDocumentModal = this.matDialog.open(DigitalLogFieldDocumentComponent, {
			disableClose: true,
			panelClass: 'field-doc-overlay-pane',
			data: config
		});

		fieldDocumentModal
			.afterClosed()
			.subscribe(
				() => {
					this.currentField = {};
					field.documentCount = this.currentFieldDocs.filter(x => x.jobDigitalLogFormTimeSlotId == field.jobDigitalLogFormTimeSlotId).length;
					//this.getFieldsAttchaments();
				});
	}

	public editFieldDocument(updatedFieldDocument: JobDigitalLogFieldUploadDocumentPayload): void {
		if (updatedFieldDocument.jobDigitalLogFormTimeSlot.jobDigitalLogFormTimeSlotId === null) {
			updatedFieldDocument.jobDigitalLogFormTimeSlot.jobDigitalLogFormTimeSlotId = 0;
		}

		this.isfieldDocumentsLoading$.next(true);

		const data = {
			digitalLogFormFieldId: updatedFieldDocument.digitalLogFormFieldId,
			documentClassAndTypeMappingId: updatedFieldDocument.documentClassAndTypeMappingId,
			timeSlot: updatedFieldDocument.jobDigitalLogFormTimeSlot,
			documentContainerId: updatedFieldDocument.documentContainerId || 0,
			documentLabel: updatedFieldDocument.documentLabel,
			description: updatedFieldDocument.description,
			listOrder: updatedFieldDocument.listOrder,
			isDeprecated: updatedFieldDocument.isDeprecated,
			documentId: updatedFieldDocument.documentId
		};

		this.digitalLogService.updateFieldDocument(
			TokenHelper.getToken(this.activatedRoute),
			this.currentField.jobDigitalLogFormRequestId,
			updatedFieldDocument.ytdReportYear,
			data
		).subscribe(
			response => {
				if (!updatedFieldDocument.jobDigitalLogFormTimeSlot.jobDigitalLogFormTimeSlotId && !!response.jobDigitalLogFormTimeSlot.jobDigitalLogFormTimeSlotId) {
					this.onTimeSlotUpdated(response.jobDigitalLogFormTimeSlot);
				}

				if (updatedFieldDocument.fieldDocument?.jobDigitalLogFormFieldDocId > 0) {
					this.currentFieldDocs = this.currentFieldDocs.map(doc => {
						return doc.jobDigitalLogFormFieldDocId === updatedFieldDocument.fieldDocument.jobDigitalLogFormFieldDocId
							? response.fieldDocument
							: doc;
					});
				}
				else {
					this.currentFieldDocs = [
						...this.currentFieldDocs,
						response.fieldDocument
					];
				}

				this.fieldDocuments$.next(this.currentFieldDocs);
				this.isfieldDocumentsLoading$.next(false);
			},
			() => {
				this.isfieldDocumentsLoading$.next(false);

				this.fieldDocumentErrorModal.open();
				this.currentFieldDocs = [...this.currentFieldDocs];
				this.fieldDocuments$.next(this.currentFieldDocs);
			});
	}

	public changeFieldDocumentListOrder(payload: FieldDocumentListOrderItemPayload): void {
		this.isfieldDocumentsLoading$.next(true);

		const docsListOrder = payload.documents.map(doc => ({ jobDocumentId: doc.jobDigitalLogFormFieldDocId, listOrder: doc.listOrder } as FieldDocumentListOrderItem));

		this.digitalLogService.updateFieldDocumentListOrder(
			TokenHelper.getToken(this.activatedRoute),
			this.currentField.jobDigitalLogFormRequestId,
			payload.ytdReportYear,
			docsListOrder,
			payload.digitalLogFormFieldId
		).subscribe(
			response => {
				if (response) {
					this.currentFieldDocs = this.currentFieldDocs.map(item => {
						return payload.documents.find(obj => obj.jobDigitalLogFormFieldDocId === item.jobDigitalLogFormFieldDocId) || item;
					});
					this.currentFieldDocs = this.currentFieldDocs.sort((a, b) => a.listOrder - b.listOrder);
				} else {
					this.fieldDocumentErrorModal.open();
				}

				this.fieldDocuments$.next(this.currentFieldDocs);
				this.isfieldDocumentsLoading$.next(false);
			},
			() => {
				this.isfieldDocumentsLoading$.next(false);
				this.fieldDocumentErrorModal.open();
				this.currentFieldDocs = [...this.currentFieldDocs];
				this.fieldDocuments$.next(this.currentFieldDocs);
			});
	}

	onFavoriteClick(field: FieldAttachment) {
		this.isLoading = true;
		this.digitalLogService
			.updateFieldAttachment(this.activeRoute.snapshot.paramMap.get('token'), { ...field, isFavorite: !field.isFavorite })
			.pipe(
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				result => {
					this.isLoading = false;

					if (result) {
						field.isFavorite = !field.isFavorite;
					}
				},
				() => {
					this.isLoading = false;
				}
			);
	}

	getFieldDocuments(jobDigitalLogFormRequestId, digitalLogFormFieldId) {
		this.fieldDocuments$.next([]);
		this.digitalLogService
			.getFieldDocuments(this.activeRoute.snapshot.paramMap.get('token'), jobDigitalLogFormRequestId, digitalLogFormFieldId)
			.pipe(
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				result => {
					this.currentFieldDocs = result;
					this.fieldDocuments$.next(this.currentFieldDocs);
					this.isfieldDocumentsLoading$.next(false);
				},
				() => {
					this.isfieldDocumentsLoading$.next(false);
					this.currentFieldDocs = [];
					this.fieldDocuments$.next(this.currentFieldDocs);
				}
			)
	}

	public isFormOnDemand(): boolean {
		return true;
	}

	public closeFieldDocumentErrorModal(): void {
		this.fieldDocumentErrorModal.hide();
	}

	private registerDocumentsModalEvents() {
		this.onUploadFieldDocument
			.pipe(
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				uploadedFieldDocument => {
					this.uploadFieldDocument(uploadedFieldDocument);
				}
			);
		this.onRemoveFieldDocument
			.pipe(
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				uploadedFieldDocument => {
					this.removeFieldDocument(uploadedFieldDocument);
				}
			);
		this.onEditFieldDocument
			.pipe(
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				uploadedFieldDocument => {
					this.editFieldDocument(uploadedFieldDocument);
				}
			);
		this.onFieldDocumentListOrderChange
			.pipe(
				takeUntil(this.unsubscribe)
			)
			.subscribe(
				uploadedFieldDocument => {
					this.changeFieldDocumentListOrder(uploadedFieldDocument);
				}
			);
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}
}